import getItemIdFromInternetMessageId from '../utils/getItemIdFromInternetMessageId';
import isPopoutLeanBootstrapEnabled from '../utils/isPopoutLeanBootstrapEnabled';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import { isPublicFolder } from 'owa-folders';
import { getGroupMailboxInfo } from 'owa-group-common';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getMailboxInfoFromFolderId } from 'owa-mail-mailboxinfo/lib/getMailboxInfo';
import getLatestNonDraftItemIdFromConversation from 'owa-mail-message-actions/lib/utils/getLatestNonDraftItemIdFromConversation';
import { lazyLoadConversationReadingPane } from 'owa-mail-reading-pane-store';
import loadItemReadingPane from 'owa-mail-reading-pane-store/lib/actions/loadItemReadingPane';
import setItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/actions/setItemReadingPaneViewState';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import ReadingPanePopoutItemFolderInfo from 'owa-popout-utils/lib/schema/ReadingPanePopoutItemFolderInfo';
import { PopoutChildState, setPopoutChildState } from 'owa-popout-v2';
import getDataFromParentWindow from 'owa-popout-v2/lib/utils/getDataFromParentWindow';
import { lazyOnSmimeItemAttachmentOpened } from 'owa-smime';
import { action } from 'satcheljs/lib/legacy';
import {
    selectDefaultFolder,
    loadPopoutDataToFolderStore,
    loadPopoutDataToPublicFolderFavoriteStore,
    selectFolder,
} from 'owa-mail-folder-forest-actions';
import '../orchestrators/closeDeeplinkReadingPaneOrchestrator';
import { getQueryStringParameter } from 'owa-querystring';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyGetGroupDetailsAction } from 'owa-groups-shared-actions';

export interface MailModuleRouteParamaters {
    itemId?: string;
    readonlyItemId?: string;
    base64EncodedInternetMessageId?: string;
    convId?: string;
    groupDomain?: string;
    groupAlias?: string;
    isReadonly?: string;
}

function getMailBoxInfo(parameters: MailModuleRouteParamaters, folderId: string) {
    if (parameters.groupDomain && parameters.groupAlias) {
        return getGroupMailboxInfo(`${parameters.groupAlias}@${parameters.groupDomain}`);
    }

    return getMailboxInfoFromFolderId(folderId);
}

export let deepLinkMailModuleRouteHandler = action('deepLinkMailModuleRouteHandler')(
    async function deepLinkMailModuleRouteHandler(parameters: MailModuleRouteParamaters) {
        const popoutData = await getDataFromParentWindow<ReadingPanePopoutItemFolderInfo>();
        readingPaneStore.isBohemiaEnabledOverride = popoutData?.includeBohemiaOnPopout;
        const instrumentationContext = popoutData?.instrumentationContext;
        let { itemId, readonlyItemId, convId, base64EncodedInternetMessageId } = parameters;
        // Since S/MIME attachments have "smime-" prefix and the parentFolderId is null, we need not convert the itemId
        itemId = isSmimeAttachment(itemId) ? itemId : convertRestIdToEwsId(itemId);
        convId = convertRestIdToEwsId(convId);

        if (readonlyItemId) {
            itemId = convertRestIdToEwsId(readonlyItemId);
        }

        if (popoutData?.folderId) {
            if (isPublicFolder(popoutData.folderId)) {
                loadPopoutDataToPublicFolderFavoriteStore(popoutData);
            } else {
                loadPopoutDataToFolderStore(popoutData);
            }
            selectFolder(popoutData.folderId, popoutData.treeType, 'DeepLink' /* ActionSource */);
        } else {
            // If popout lean bootstrap is enabled, don't select default folder since
            // select default folder will load initial table view(FindConversation) and
            // prefetch First N (GetConversationItems or GetItem).
            !isPopoutLeanBootstrapEnabled() && selectDefaultFolder('DeepLink');
        }
        const mailboxInfo = getMailBoxInfo(parameters, popoutData?.folderId);
        if (popoutData?.smimeAttachmentItem) {
            // populate the mailStore with the SmimeItem so that loadItemReadingPane
            // does not try to fetch it from server
            await lazyOnSmimeItemAttachmentOpened.importAndExecute(
                itemId,
                popoutData.smimeAttachmentItem,
                mailboxInfo
            );
        }

        if (!itemId) {
            if (convId) {
                const loadConversationReadingPane = await lazyLoadConversationReadingPane.import();

                const conversationId: ClientItemId = {
                    Id: convId,
                    mailboxInfo: mailboxInfo,
                };
                await loadConversationReadingPane(
                    conversationId,
                    instrumentationContext,
                    null /*conversation.subject*/
                );

                const latestMessageId = await getLatestNonDraftItemIdFromConversation(
                    conversationId
                );
                if (latestMessageId) {
                    itemId = latestMessageId.Id;
                }
            } else if (base64EncodedInternetMessageId) {
                itemId = await getItemIdFromInternetMessageId(
                    decodeURIComponent(atob(base64EncodedInternetMessageId))
                );
            }
        }

        if (itemId) {
            loadItemReadingPane(
                { Id: itemId, mailboxInfo: mailboxInfo },
                instrumentationContext,
                viewState => {
                    viewState.isReadonly = !!readonlyItemId;
                    setItemReadingPaneViewState(viewState);
                    readingPaneStore.deeplinkId = {
                        id: viewState.itemId,
                        isConversation: false,
                        mailboxInfo: mailboxInfo,
                    };
                }
            );
        }

        setPopoutChildState(PopoutChildState.Ready);

        const useProxyEmail = getQueryStringParameter('useProxyEmail');
        if (
            isFeatureEnabled('cmp-proxy-address-sendas-param') &&
            mailboxInfo.type == MailboxType.GroupMailbox &&
            useProxyEmail
        ) {
            const { mailboxSmtpAddress } = mailboxInfo;
            await lazyGetGroupDetailsAction.importAndExecute(mailboxSmtpAddress);
        }
    }
);
